import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Plus, Search, RefreshCw, Receipt, Clock, CheckCircle, XCircle, X, DollarSign, FileText, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function ClaimsManagement({ user, onLogout }) {
  const [claims, setClaims] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('my-claims');
  const [selectedClaim, setSelectedClaim] = useState(null);
  
  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [formData, setFormData] = useState({
    expense_type: 'travel',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    receipt_url: ''
  });

  const expenseTypes = ['travel', 'accommodation', 'meals', 'fuel', 'internet', 'phone', 'miscellaneous'];

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [claimsRes, approvalsRes] = await Promise.allSettled([
        axios.get(`${API}/api/travel-claims/my-claims`, { headers }),
        axios.get(`${API}/api/travel-claims/pending-approvals`, { headers })
      ]);

      setClaims(claimsRes.status === 'fulfilled' ? (claimsRes.value.data.claims || []) : []);
      setPendingApprovals(approvalsRes.status === 'fulfilled' ? (approvalsRes.value.data.claims || []) : []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter claims
  const filteredClaims = claims.filter(claim => {
    const matchesSearch = !searchTerm || 
      claim.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.expense_type?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || claim.expense_type === filterType;
    const matchesStatus = filterStatus === 'all' || claim.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  // Statistics
  const stats = {
    total: claims.length,
    pending: claims.filter(c => c.status === 'pending' || c.status === 'Pending').length,
    approved: claims.filter(c => c.status === 'approved' || c.status === 'Approved').length,
    totalAmount: claims.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0)
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/travel-claims/manual`, {
        ...formData,
        amount: parseFloat(formData.amount),
        claim_type: 'manual'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Claim submitted successfully!');
      setShowForm(false);
      resetForm();
      fetchAllData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (claimId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/travel-claims/${claimId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Claim approved!');
      fetchAllData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleReject = async (claimId) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/travel-claims/${claimId}/reject`, { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Claim rejected!');
      fetchAllData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      expense_type: 'travel',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      description: '',
      receipt_url: ''
    });
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase();
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-700 border-l-yellow-500',
      'approved': 'bg-green-100 text-green-700 border-l-green-500',
      'rejected': 'bg-red-100 text-red-700 border-l-red-500',
      'paid': 'bg-blue-100 text-blue-700 border-l-blue-500'
    };
    return colors[s] || colors['pending'];
  };

  const getStatusBadgeColor = (status) => {
    const s = status?.toLowerCase();
    const colors = {
      'pending': 'bg-yellow-500 text-white',
      'approved': 'bg-green-500 text-white',
      'rejected': 'bg-red-500 text-white',
      'paid': 'bg-blue-500 text-white'
    };
    return colors[s] || colors['pending'];
  };

  const canApprove = user?.role === 'Manager' || user?.role === 'HR' || user?.role === 'Admin' || user?.role === 'HR Manager';

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>💰 Claims Management</h1>
            <p className="text-gray-600 mt-2">Submit and track expense claims and reimbursements</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchAllData}
              disabled={loading}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
              style={{backgroundColor: '#2b3380'}}
            >
              <Plus className="w-5 h-5" />
              Submit Claim
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full" style={{backgroundColor: '#e8eaf6'}}>
              <Receipt className="w-6 h-6" style={{color: '#2b3380'}} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{color: '#2b3380'}}>{stats.total}</p>
              <p className="text-sm text-gray-500">Total Claims</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-yellow-100">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              <p className="text-sm text-gray-500">Pending</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-green-100">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
              <p className="text-sm text-gray-500">Approved</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-blue-100">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">₹{stats.totalAmount.toLocaleString()}</p>
              <p className="text-sm text-gray-500">Total Amount</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('my-claims')}
              className={`px-6 py-3 font-medium ${activeTab === 'my-claims' ? 'border-b-2 text-blue-600' : 'text-gray-500'}`}
              style={activeTab === 'my-claims' ? {borderColor: '#2b3380', color: '#2b3380'} : {}}
            >
              My Claims ({claims.length})
            </button>
            {canApprove && (
              <button
                onClick={() => setActiveTab('approvals')}
                className={`px-6 py-3 font-medium ${activeTab === 'approvals' ? 'border-b-2 text-blue-600' : 'text-gray-500'}`}
                style={activeTab === 'approvals' ? {borderColor: '#2b3380', color: '#2b3380'} : {}}
              >
                Pending Approvals ({pendingApprovals.length})
              </button>
            )}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search claims..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              {expenseTypes.map(type => (
                <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Claims List */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            <p className="mt-3 text-gray-500">Loading claims...</p>
          </div>
        ) : activeTab === 'my-claims' ? (
          filteredClaims.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <Receipt className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-xl font-medium text-gray-500 mb-2">No claims found</p>
              <p className="text-gray-400">Click "Submit Claim" to create your first expense claim</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredClaims.map((claim) => (
                <div 
                  key={claim.id} 
                  className={`bg-white rounded-lg shadow-md p-6 border-l-4 hover:shadow-lg transition-shadow ${getStatusColor(claim.status)}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold" style={{color: '#2b3380'}}>
                          {claim.expense_type?.charAt(0).toUpperCase() + claim.expense_type?.slice(1)} Expense
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(claim.status)}`}>
                          {claim.status?.toUpperCase()}
                        </span>
                        {claim.claim_type && (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            {claim.claim_type}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold" style={{color: '#2b3380'}}>₹{parseFloat(claim.amount).toLocaleString()}</p>
                    </div>
                  </div>
                  
                  {claim.description && (
                    <p className="text-gray-700 mb-3">{claim.description}</p>
                  )}
                  
                  <div className="flex flex-wrap justify-between items-center text-sm text-gray-500 pt-3 border-t">
                    <div className="flex items-center gap-4">
                      <span>Date: {claim.date ? new Date(claim.date).toLocaleDateString('en-IN') : 'N/A'}</span>
                      {claim.receipt_url && (
                        <a href={claim.receipt_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 flex items-center gap-1">
                          <FileText className="w-4 h-4" /> View Receipt
                        </a>
                      )}
                    </div>
                    <span>Submitted: {claim.created_at ? new Date(claim.created_at).toLocaleDateString('en-IN') : 'N/A'}</span>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          /* Pending Approvals Tab */
          pendingApprovals.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <CheckCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-xl font-medium text-gray-500 mb-2">No pending approvals</p>
              <p className="text-gray-400">All claims have been processed</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingApprovals.map((claim) => (
                <div 
                  key={claim.id} 
                  className="bg-white rounded-lg shadow-md p-6 border-l-4 border-l-yellow-500 hover:shadow-lg transition-shadow"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold" style={{color: '#2b3380'}}>
                          {claim.expense_type?.charAt(0).toUpperCase() + claim.expense_type?.slice(1)} Expense
                        </h3>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-500 text-white">
                          PENDING
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">Submitted by: {claim.employee_name || 'Unknown'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold" style={{color: '#2b3380'}}>₹{parseFloat(claim.amount).toLocaleString()}</p>
                    </div>
                  </div>
                  
                  {claim.description && (
                    <p className="text-gray-700 mb-3">{claim.description}</p>
                  )}
                  
                  <div className="flex justify-between items-center pt-3 border-t">
                    <span className="text-sm text-gray-500">
                      Date: {claim.date ? new Date(claim.date).toLocaleDateString('en-IN') : 'N/A'}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApprove(claim.id)}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-1"
                      >
                        <CheckCircle className="w-4 h-4" /> Approve
                      </button>
                      <button
                        onClick={() => handleReject(claim.id)}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-1"
                      >
                        <XCircle className="w-4 h-4" /> Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* Submit Claim Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>💰 Submit Expense Claim</h2>
                  <button onClick={() => { setShowForm(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Expense Type *</label>
                      <select
                        value={formData.expense_type}
                        onChange={(e) => setFormData({...formData, expense_type: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        {expenseTypes.map(type => (
                          <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Amount (₹) *</label>
                        <input
                          type="number"
                          value={formData.amount}
                          onChange={(e) => setFormData({...formData, amount: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                          min="1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Date *</label>
                        <input
                          type="date"
                          value={formData.date}
                          onChange={(e) => setFormData({...formData, date: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Description *</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        rows="3"
                        required
                        placeholder="Describe the expense..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Receipt URL</label>
                      <input
                        type="url"
                        value={formData.receipt_url}
                        onChange={(e) => setFormData({...formData, receipt_url: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="https://..."
                      />
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-700">
                        <strong>Note:</strong> Your claim will be submitted for approval. You will be notified once it's processed.
                      </p>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="px-6 py-3 text-white rounded-lg font-medium flex items-center gap-2"
                        style={{backgroundColor: submitting ? '#9e9e9e' : '#2b3380'}}
                      >
                        <Receipt className="w-5 h-5" />
                        {submitting ? 'Submitting...' : 'Submit Claim'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowForm(false); resetForm(); }}
                        className="px-6 py-3 border rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default ClaimsManagement;
